import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { APP_ENV, ENV_NAMES, graphqlErrorHandler, nonNullable } from "@utils/utils";
import { GraphQLError } from "graphql";
import React, { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Dialog, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerFileFileKindChoices,
  ApiReportSectionsAiGeneratedStatusChoices,
  ReportPageLocationEnums,
  useCreateReportTemplateSectionsMutation,
  usePopulateDataFromBhtiaMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { getOrderedAssessmentReportSubRoutes } from "@pages/AssessmentReportV2Details/helpers";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";
import { useAdminData } from "@providers/AdminDataProvider";
import { ReportNode, useReport } from "@providers/ReportProvider";

interface ConfirmGenerationContentProps {
  type: ReportPageLocationEnums;
  promptDialogOpen: boolean;
  setPromptDialogOpen: (bool: boolean) => void;
  onboardingData?: string;
  skipNavigate?: boolean;
}

export const GenerationTypeLabelMapping: { [key in ReportPageLocationEnums]: string } = {
  [ReportPageLocationEnums.ReportPart_1]: "Report Part 1",
  [ReportPageLocationEnums.ReportPart_2]: "Report Part 2",
  [ReportPageLocationEnums.Conclusions]: "Conclusions",
  [ReportPageLocationEnums.Recommendations]: "Recommendations",
  [ReportPageLocationEnums.Appendix]: "Appendix",
  [ReportPageLocationEnums.TreatmentPlan]: "Treatment Plan",
};

const checkIsPredictionGenerating = (page: ReportPageLocationEnums, report: ReportNode): boolean => {
  return report.reportSections?.edges
    .map((edge) => edge?.node)
    .filter((section) => section?.pageLocation === page)
    .some(
      (section) => section?.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending,
    );
};

const checkIsPredictionGeneratedAlready = (page: ReportPageLocationEnums, report: ReportNode): boolean => {
  return report.reportSections.edges
    .map((edge) => edge?.node)
    .filter((section) => section?.pageLocation === page)
    .some(
      (section) => section?.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted,
    );
};

export const ConfirmGenerationDialog: React.FC<ConfirmGenerationContentProps> = ({
  type,
  promptDialogOpen,
  setPromptDialogOpen,
  skipNavigate = false,
}) => {
  const { pathname } = useLocation();
  const go = useNavigate();
  const { report, reportQuery } = useReport();
  const reportId = report.id;
  const { isKuwaitUser, roles } = useAdminData();
  const isInternalUser = roles.includes("frontera-internal");
  const files = report.reportFiles.edges?.map((edge) => edge?.node);
  const filesForAssessment = files?.filter((file) => file?.status !== "PENDING")?.filter(nonNullable);
  const existingBHTIAFiles = filesForAssessment?.filter((file) =>
    [ApiLearnerFileFileKindChoices.Bhtia].includes(file.fileKind),
  );
  const isPredictionGenerating = checkIsPredictionGenerating(type, report);
  const isPredictionGeneratedAlready = checkIsPredictionGeneratedAlready(type, report);

  const createReportTemplateSections = useCreateReportTemplateSectionsMutation({});
  const populateDataFromBHTIA = usePopulateDataFromBhtiaMutation({});

  const [showNotice, setShowNotice] = React.useState<boolean>(isPredictionGeneratedAlready || false);

  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as InitialAssessmentPageRoute) ||
    ("upload-files" as InitialAssessmentPageRoute);

  const { getNextRoute } = getOrderedAssessmentReportSubRoutes();
  const nextRoute = getNextRoute(currentRoute);

  const generationTypeLabel = GenerationTypeLabelMapping[type];

  const handleDialogClick = useCallback(
    async (skipAIGeneration: boolean = false) => {
      if (isPredictionGenerating) {
        setPromptDialogOpen(false);
      } else if (showNotice && [ENV_NAMES.DEV, ENV_NAMES.LOCAL].includes(APP_ENV)) {
        setShowNotice(false);
      } else {
        try {
          // First mutation - create report template sections
          await createReportTemplateSections.mutateAsync({
            input: {
              reportId: reportId || "<missing-report-id>",
              pageLocation: type,
              skipAiGeneration: skipAIGeneration,
              useConverter: true,
            },
          });

          // Second mutation - populate data from BHTIA (only if BHTIA files exist)
          if (existingBHTIAFiles && existingBHTIAFiles.length > 0 && type === ReportPageLocationEnums.ReportPart_1) {
            await populateDataFromBHTIA.mutateAsync({
              learnerFileId: existingBHTIAFiles[0].id,
              reportId: reportId || "",
              pageLocation: type,
            });
          }

          // Close the dialog first
          setPromptDialogOpen(false);

          if (skipNavigate) {
            reportQuery.refetch();
          } else {
            go(`../${nextRoute}`);
          }
        } catch (err) {
          const errorMessages = graphqlErrorHandler(err as GraphQLError);
          for (const errorMessage of errorMessages) {
            notifyError(errorMessage);
          }
          console.error(`Failed to kickoff ${generationTypeLabel} Generation`, err);
          setPromptDialogOpen(false);
          if (!skipNavigate) {
            go(`../${nextRoute}`);
          }
        }
      }
    },
    [
      isPredictionGenerating,
      showNotice,
      createReportTemplateSections,
      populateDataFromBHTIA,
      existingBHTIAFiles,
      reportId,
      type,
      setPromptDialogOpen,
      go,
      nextRoute,
      generationTypeLabel,
      skipNavigate,
      reportQuery,
    ],
  );

  useEffect(() => {
    if (promptDialogOpen && isKuwaitUser) {
      handleDialogClick();
    }
  }, [promptDialogOpen]);

  if (isKuwaitUser) return null;

  return (
    <Dialog
      size="xl"
      open={promptDialogOpen}
      setOpen={setPromptDialogOpen}
      title={showNotice || isPredictionGenerating ? "Notice" : `Confirm ${generationTypeLabel} Generation`}
      primaryButton={{
        text: createReportTemplateSections.isPending
          ? "Generating..."
          : showNotice
            ? "Yes, Continue"
            : isPredictionGenerating
              ? "Okay"
              : "Generate",
        buttonAction: showNotice ? "destructive" : "regular",
        onClick: () => handleDialogClick(false),
        disabled: createReportTemplateSections.isPending,
        className: "px-6",
      }}
      secondaryButton={
        !showNotice && isPredictionGenerating
          ? undefined
          : {
              text: showNotice ? "No, Cancel" : "Cancel",
              onClick: () => {
                if (showNotice) {
                  setPromptDialogOpen(false);
                } else {
                  setPromptDialogOpen(false);
                  if (isPredictionGeneratedAlready) setShowNotice(true);
                }
              },
              className: "px-6 hover:no-underline",
            }
      }
      tertiaryButton={
        isInternalUser && type == ReportPageLocationEnums.TreatmentPlan
          ? {
              text: "Skip Generation",
              buttonAction: "action",
              onClick: () => handleDialogClick(true),
              disabled: createReportTemplateSections.isPending,
              className: "px-6",
            }
          : undefined
      }
    >
      {showNotice ? (
        <>
          <Paragraph displayType="normal" colorType="secondary">
            You are about to resubmit a previous step. This will cause the upcoming pages of your assessment report to
            be regenerated. Any changes you previously made to these pages will be overwritten.
          </Paragraph>
          <Paragraph displayType="normal" colorType="secondary" className="my-4">
            Would you like to proceed?
          </Paragraph>
          <div className="p-4 bg-red-50">
            <Paragraph displayType="loud" className="inline text-red-500!">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 inline mr-2" />
              Warning:{" "}
            </Paragraph>
            <Paragraph displayType="normal" colorType="secondary" className="inline text-red-500!">
              &nbsp; This action is not reversible
            </Paragraph>
          </div>
        </>
      ) : isPredictionGenerating ? (
        <>
          <Paragraph displayType="normal" colorType="secondary">
            Content for another step is currently being generated. This may take a few moments. Please wait until the
            process is complete before proceeding.
          </Paragraph>
        </>
      ) : (
        <Small>
          By clicking "Generate", you will be kicking off the {generationTypeLabel} generation step. This step will take
          several minutes. While {generationTypeLabel} generation is happening, you will not be able to edit your
          report.
        </Small>
      )}
    </Dialog>
  );
};
